import { useState, useEffect, useCallback, useMemo } from 'react';
import { Country } from 'country-state-city';
import * as dashboardApi from '../../../api/dashboardApi';
import type { TrendingTopic } from '../../../api/dashboardApi';
import { getCoordinatesForJurisdiction } from '../../../utils/jurisdictionCoordinates';
import { getMapLayers } from '../../../api/calendarApi';

export type TimeRangeType = 'All' | '24H' | '7D' | '30D' | '90D' | '1Y' | 'Custom';
export type DateFilterType = 'last_action_date' | 'published_date' | 'both' | 'date_added';

const SUPRANATIONALS_FULL: Record<string, string> = {
    EU: 'European Union',
    UN: 'United Nations',
    OECD: 'OECD',
    GCC: 'Gulf Cooperation Council',
    ASEAN: 'ASEAN',
    APEC: 'APEC',
    AU: 'African Union',
};
const SUPRA_CODES = new Set(Object.keys(SUPRANATIONALS_FULL));

interface FilterState {
    selectedJurisdiction: string;
    selectedTimeRange: TimeRangeType;
    dateFilterType: DateFilterType;
    customStartDate: string;
    customEndDate: string;
    selectedCountry: string;
    selectedContinent: string;
    activeFeedTab: string;
    getDateRangeForAPI: () => { start_date?: string; end_date?: string; days?: number };
}

export function useDashboardData(token: string | null, filters: FilterState) {
    const {
        selectedJurisdiction, selectedTimeRange, dateFilterType,
        customStartDate, customEndDate, selectedCountry,
        selectedContinent, activeFeedTab, getDateRangeForAPI,
    } = filters;

    // ── KPI state ──────────────────────────────────────────────────────────────
    const [totalJurisdictions, setTotalJurisdictions] = useState<number | null>(null);
    const [totalRegulations, setTotalRegulations] = useState<number | null>(null);
    const [newslettersCount, setNewslettersCount] = useState<number>(0); // eslint-disable-line @typescript-eslint/no-unused-vars
    const [countriesTracked, setCountriesTracked] = useState<number | null>(null);
    const [animatedStats, setAnimatedStats] = useState<number[]>([0, 0]);

    // ── Map / jurisdiction state ────────────────────────────────────────────────
    const [jurisdictions, setJurisdictions] = useState<dashboardApi.Jurisdiction[]>([]);
    const [jurisdictionStats, setJurisdictionStats] = useState<dashboardApi.JurisdictionStats[]>([]);
    const [countryStats, setCountryStats] = useState<Record<string, number>>({});
    // SRS #10: live map layers (activity / upcoming / consultations / enforcement)
    const [mapLayers, setMapLayers] = useState<{ layers: Record<string, Record<string, number>>; labels: Record<string, string> } | null>(null);
    useEffect(() => { getMapLayers().then(r => setMapLayers({ layers: r.layers, labels: r.labels })).catch(() => setMapLayers(null)); }, []);
    // When the jurisdiction snapshot was computed server-side (map data-freshness).
    const [dataComputedAt, setDataComputedAt] = useState<string | null>(null);

    // ── Feed state ─────────────────────────────────────────────────────────────
    const [recentRegulations, setRecentRegulations] = useState<dashboardApi.RegulationSummary[]>([]);
    const [trendData, setTrendData] = useState<dashboardApi.TrendDataPoint[]>([]);
    const [chatTrendingTopics, setChatTrendingTopics] = useState<TrendingTopic[]>([]);

    // ── Filter option state ────────────────────────────────────────────────────
    const [availableJurisdictionTypes, setAvailableJurisdictionTypes] = useState<string[]>([]);
    const [availableRegulationTypes, setAvailableRegulationTypes] = useState<string[]>([]);
    const [jurisdictionFilters, setJurisdictionFilters] = useState<Record<string, boolean>>({});
    const [policyTypeFilters, setPolicyTypeFilters] = useState<Record<string, boolean>>({});

    // ── Loading states ─────────────────────────────────────────────────────────
    const [isLoadingJurisdictions, setIsLoadingJurisdictions] = useState(false);
    const [isLoadingRegulations, setIsLoadingRegulations] = useState(false);
    const [isLoadingTrends, setIsLoadingTrends] = useState(false);
    const [isLoadingStats] = useState(false);
    const [isLoadingChatTopics, setIsLoadingChatTopics] = useState(false);

    // ── Initial KPI + all-time stats ───────────────────────────────────────────
    useEffect(() => {
        const run = async () => {
            const [jurisResult, newslettersResult, allTimeStatsResult, totalRegulationsResult] = await Promise.allSettled([
                dashboardApi.getTotalJurisdictions(),
                dashboardApi.getNewslettersCount(),
                dashboardApi.getJurisdictionStats({}),
                dashboardApi.getTotalRegulations(),
            ]);

            if (jurisResult.status === 'fulfilled') setTotalJurisdictions(jurisResult.value?.total ?? 0);
            if (newslettersResult.status === 'fulfilled') {
                const d = newslettersResult.value;
                if (d && typeof d.total === 'number') setNewslettersCount(d.total);
            }
            if (allTimeStatsResult.status === 'fulfilled') {
                const d = allTimeStatsResult.value;
                setJurisdictionStats(d?.stats ?? []);
                setCountryStats(d?.by_country ?? {});
                setDataComputedAt(d?.computed_at ?? null);
                setCountriesTracked(
                    Object.keys(d?.by_country ?? {})
                        .filter(k => !SUPRA_CODES.has(k) && !!Country.getCountryByCode(k))
                        .length
                );
            }
            if (totalRegulationsResult.status === 'fulfilled') {
                setTotalRegulations(totalRegulationsResult.value?.total ?? 0);
            }
        };
        run();
    }, [token]);

    // ── Animated stat numbers ──────────────────────────────────────────────────
    useEffect(() => {
        if (totalRegulations === null || totalJurisdictions === null) return;
        const targets = [totalRegulations, totalJurisdictions];
        if (process.env.NODE_ENV === 'test') { setAnimatedStats(targets); return; }
        const duration = 1000;
        const start = Date.now();
        const tick = () => {
            const p = Math.min((Date.now() - start) / duration, 1);
            setAnimatedStats(targets.map(t => Math.floor(t * p)));
            if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [totalRegulations, totalJurisdictions]);

    // ── Jurisdictions list + filter options on country change ──────────────────
    useEffect(() => {
        setIsLoadingJurisdictions(true);
        Promise.allSettled([
            dashboardApi.getJurisdictions({ country_id: (!selectedCountry || selectedCountry.startsWith('__supra__')) ? undefined : selectedCountry }),
            dashboardApi.getFilterOptions(),
        ]).then(([jurisRes, filterRes]) => {
            if (jurisRes.status === 'fulfilled') setJurisdictions(jurisRes.value);
            const opts = filterRes.status === 'fulfilled' ? filterRes.value : null;
            if (!opts?.jurisdiction_types || !opts?.regulation_types) {
                setAvailableJurisdictionTypes([]);
                setAvailableRegulationTypes([]);
                setJurisdictionFilters({});
                setPolicyTypeFilters({});
            } else {
                setAvailableJurisdictionTypes(opts.jurisdiction_types);
                setAvailableRegulationTypes(opts.regulation_types);
                const jf: Record<string, boolean> = {};
                opts.jurisdiction_types.forEach((t: string) => { jf[t] = true; });
                setJurisdictionFilters(jf);
                const pf: Record<string, boolean> = {};
                opts.regulation_types.forEach((t: string) => { pf[t] = true; });
                setPolicyTypeFilters(pf);
            }
        }).finally(() => setIsLoadingJurisdictions(false));
    }, [selectedCountry]);

    // ── Recent regulations ─────────────────────────────────────────────────────
    const fetchRecentRegulations = useCallback(async (explicitParams?: any) => {
        setIsLoadingRegulations(true);
        try {
            let params: any = {};
            if (explicitParams !== undefined) {
                params = explicitParams;
            } else {
                const dateRange = getDateRangeForAPI();
                params = {
                    jurisdiction_code: selectedJurisdiction !== 'All' ? selectedJurisdiction : undefined,
                    date_type: dateFilterType,
                    country_id: (selectedCountry && !selectedCountry.startsWith('__supra__')) ? selectedCountry : undefined,
                };
                if (dateRange.start_date && dateRange.end_date) {
                    params.start_date = dateRange.start_date;
                    params.end_date = dateRange.end_date;
                } else if ((dateRange as any).days) {
                    params.days = (dateRange as any).days;
                } else if (selectedTimeRange !== 'All') {
                    params.days = 7;
                }
            }
            const data = await dashboardApi.getRecentRegulations(params);
            setRecentRegulations(data?.regulations ?? []);
        } catch (e) {
            console.error('Error fetching recent regulations:', e);
        } finally {
            setIsLoadingRegulations(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedJurisdiction, selectedTimeRange, dateFilterType, customStartDate, customEndDate, selectedCountry]);

    useEffect(() => { fetchRecentRegulations(); }, [fetchRecentRegulations]);

    // ── Trend data ─────────────────────────────────────────────────────────────
    useEffect(() => {
        const run = async () => {
            setIsLoadingTrends(true);
            try {
                const dateRange = getDateRangeForAPI();
                const params: any = {
                    jurisdiction_code: selectedJurisdiction !== 'All' ? selectedJurisdiction : undefined,
                    date_type: dateFilterType,
                    granularity: 'month',
                };
                if (dateRange.start_date && dateRange.end_date) {
                    params.start_date = dateRange.start_date;
                    params.end_date = dateRange.end_date;
                } else if (selectedTimeRange !== 'All') {
                    const now = new Date();
                    params.end_date = now.toISOString().split('T')[0];
                    const s = new Date(now);
                    s.setDate(s.getDate() - 365);
                    params.start_date = s.toISOString().split('T')[0];
                }
                const data = await dashboardApi.getTrends(params);
                setTrendData(data?.data ?? []);
            } catch (e) {
                console.error('Error fetching trends:', e);
            } finally {
                setIsLoadingTrends(false);
            }
        };
        run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedJurisdiction, selectedTimeRange, dateFilterType, customStartDate, customEndDate, selectedCountry]);

    // ── Chat trending topics ───────────────────────────────────────────────────
    useEffect(() => {
        if (activeFeedTab !== 'trending') return;
        let cancelled = false;
        setIsLoadingChatTopics(true);
        dashboardApi.getTrendingTopicsFromChat(10)
            .then(res => { if (!cancelled) setChatTrendingTopics(res.topics || []); })
            .catch(() => { if (!cancelled) setChatTrendingTopics([]); })
            .finally(() => { if (!cancelled) setIsLoadingChatTopics(false); });
        return () => { cancelled = true; };
    }, [activeFeedTab]);

    // ── Derived: available places for map ─────────────────────────────────────
    const availablePlaces = useMemo(() => {
        const places: Array<{ name: string; code: string; lat: number; lng: number; count: number; mostRecent?: string | null }> = [];
        jurisdictionStats.forEach(s => {
            const coords = getCoordinatesForJurisdiction(s.jurisdiction_code);
            if (!coords) return;
            const country = Country.getCountryByCode(s.jurisdiction_code.split('-')[0]);
            const region: string = (country as any)?.region || (country as any)?.continent || '';
            if (selectedContinent && selectedContinent !== 'All') {
                if (!region || region.toLowerCase() !== selectedContinent.toLowerCase()) return;
            }
            places.push({ name: coords.label, code: s.jurisdiction_code, lat: coords.lat, lng: coords.lng, count: s.regulation_count, mostRecent: s.most_recent_regulation_date ?? null });
        });
        places.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
        return places;
    }, [jurisdictionStats, selectedContinent]);

    // ── Derived: supranational entries ────────────────────────────────────────
    const supranationalEntries = useMemo(() => {
        const counts: Record<string, number> = {};
        jurisdictionStats.forEach(s => {
            const prefix = (s.jurisdiction_code || '').split('-')[0].toUpperCase();
            if (SUPRA_CODES.has(prefix)) counts[prefix] = (counts[prefix] || 0) + s.regulation_count;
        });
        return Object.entries(SUPRANATIONALS_FULL)
            .filter(([code]) => (counts[code] ?? 0) > 0)
            .map(([code, name]) => ({ code, name, count: counts[code] }));
    }, [jurisdictionStats]);

    return {
        // KPI
        totalJurisdictions, totalRegulations, countriesTracked, animatedStats, newslettersCount,
        // Map
        jurisdictions, jurisdictionStats, countryStats, availablePlaces, dataComputedAt, mapLayers,
        // Feed
        recentRegulations, trendData, chatTrendingTopics,
        // Filter options
        availableJurisdictionTypes, availableRegulationTypes,
        jurisdictionFilters, setJurisdictionFilters,
        policyTypeFilters, setPolicyTypeFilters,
        // Loading
        isLoadingJurisdictions, isLoadingRegulations, isLoadingTrends,
        isLoadingStats, isLoadingChatTopics,
        // Derived
        supranationalEntries,
        // Actions
        fetchRecentRegulations,
    };
}
